import React, { useState, useEffect } from 'react';
import { ExternalLink, Filter, Loader, ArrowRight, Sparkles } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { useToast } from '../hooks/use-toast';
import { getImageUrlWithFallback } from '../utils/imageUtils';

const CanvaTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStyle, setSelectedStyle] = useState('all');
  const { toast } = useToast();

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const styles = [
    { value: 'all', label: 'Tutti gli stili', icon: '🎨' },
    { value: 'classico', label: 'Classico', icon: '🏛️' },
    { value: 'moderno', label: 'Moderno', icon: '✨' },
    { value: 'vintage', label: 'Vintage', icon: '📜' },
    { value: 'rustico', label: 'Rustico', icon: '🌿' },
    { value: 'elegante', label: 'Elegante', icon: '💎' }
  ];

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/canva-templates/?active_only=true`);
      
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare i template",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = selectedStyle === 'all' 
    ? templates 
    : templates.filter(t => t.style === selectedStyle);

  const getStyleColor = (style) => {
    const colors = {
      classico: 'from-blue-500 to-blue-600',
      moderno: 'from-purple-500 to-purple-600',
      vintage: 'from-amber-500 to-amber-600',
      rustico: 'from-green-500 to-green-600',
      elegante: 'from-pink-500 to-pink-600'
    };
    return colors[style] || 'from-gray-500 to-gray-600';
  };

  const getStyleBadge = (style) => {
    const badges = {
      classico: 'bg-blue-100 text-blue-800',
      moderno: 'bg-purple-100 text-purple-800',
      vintage: 'bg-amber-100 text-amber-800',
      rustico: 'bg-green-100 text-green-800',
      elegante: 'bg-pink-100 text-pink-800'
    };
    return badges[style] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 text-white py-16 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-6">
                <Sparkles className="w-10 h-10" />
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                Modelli Etichette Canva
              </h1>
              <p className="text-xl sm:text-2xl text-white/90 max-w-3xl mx-auto mb-8">
                Template professionali pronti da personalizzare per le tue etichette di liquori
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <div className="flex items-center gap-2 text-white/90">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  <span className="text-sm">{templates.length} template disponibili</span>
                </div>
                <div className="flex items-center gap-2 text-white/90">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  <span className="text-sm">100% Personalizzabili</span>
                </div>
                <div className="flex items-center gap-2 text-white/90">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  <span className="text-sm">Facili da usare</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* How it works */}
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Come Funziona
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-blue-600">1</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Scegli Template</h3>
                <p className="text-sm text-gray-600">
                  Seleziona il template che preferisci dalla nostra collezione
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-purple-600">2</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Personalizza su Canva</h3>
                <p className="text-sm text-gray-600">
                  Modifica testo, colori e immagini direttamente su Canva
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">3</span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Scarica e Ordina</h3>
                <p className="text-sm text-gray-600">
                  Scarica l'etichetta e usala per il tuo ordine personalizzato
                </p>
              </div>
            </div>
          </div>

          {/* Style Filter */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Filter className="w-5 h-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Filtra per Stile</h3>
            </div>
            <div className="flex flex-wrap gap-3">
              {styles.map((style) => (
                <button
                  key={style.value}
                  onClick={() => setSelectedStyle(style.value)}
                  className={`flex items-center gap-2 px-5 py-3 rounded-xl font-medium transition-all ${
                    selectedStyle === style.value
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg scale-105'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <span className="text-xl">{style.icon}</span>
                  <span>{style.label}</span>
                  {selectedStyle === style.value && (
                    <span className="ml-1 bg-white/20 px-2 py-0.5 rounded-full text-xs">
                      {filteredTemplates.length}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Templates Grid */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader className="w-12 h-12 text-blue-600 animate-spin mb-4" />
              <p className="text-gray-600">Caricamento template...</p>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl shadow-lg">
              <div className="text-6xl mb-4">🎨</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Nessun template trovato
              </h3>
              <p className="text-gray-600">
                {selectedStyle === 'all' 
                  ? 'Non ci sono template disponibili al momento' 
                  : 'Prova a selezionare un altro stile'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredTemplates.map((template) => (
                <div 
                  key={template.id} 
                  className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group"
                >
                  {/* Thumbnail - Always Square */}
                  <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center overflow-hidden relative">
                    {template.thumbnailUrl ? (
                      <>
                        <img 
                          src={getImageUrlWithFallback(template.thumbnailUrl)} 
                          alt={template.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = `
                              <div class="text-center p-6">
                                <svg class="w-16 h-16 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                                </svg>
                                <p class="text-gray-500">Template Canva</p>
                              </div>
                            `;
                          }}
                        />
                        <div className={`absolute inset-0 bg-gradient-to-t ${getStyleColor(template.style)} opacity-0 group-hover:opacity-20 transition-opacity`} />
                      </>
                    ) : (
                      <div className="text-center p-6">
                        <ExternalLink className="w-16 h-16 mx-auto mb-3 text-gray-400" />
                        <p className="text-gray-500">Template Canva</p>
                      </div>
                    )}
                    
                    {/* Badge Style */}
                    <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-medium ${getStyleBadge(template.style)}`}>
                      {template.style}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="font-bold text-gray-900 text-lg mb-2">{template.name}</h3>
                    
                    {template.description && (
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                        {template.description}
                      </p>
                    )}

                    {/* CTA Button */}
                    <a
                      href={template.canvaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r ${getStyleColor(template.style)} text-white rounded-xl hover:shadow-lg transition-all font-medium group-hover:gap-3`}
                    >
                      <ExternalLink className="w-5 h-5" />
                      <span>Apri in Canva</span>
                      <ArrowRight className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* CTA Section */}
          <div className="mt-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-8 py-12 sm:px-12 sm:py-16 text-center text-white">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Pronto a Creare la Tua Etichetta?
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Inizia ora con uno dei nostri template professionali e crea un'etichetta unica per il tuo liquore
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/builder"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 rounded-xl hover:bg-gray-100 transition-all font-bold text-lg"
                >
                  Crea il Tuo Liquore
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
                <a
                  href="/collections"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-xl hover:bg-white/20 transition-all font-bold text-lg border-2 border-white/30"
                >
                  Scopri le Collezioni
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CanvaTemplatesPage;
